import { LocalizationModule, LocalizationService, SubscriptionService } from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { ColDef } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, viewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AgGridComponent,
  AuditedColumns,
  BaseDapperReadListPage,
  DwGridDataSourceService,
  FieldTypes,
  IEnumerationCellRendererParams,
  ListPageComponent,
} from '@digiwall/ag-grid';
import { LanguageStore } from '@digiwall/core';
import { DwGridRequestDto, FilterModel } from '@digiwall/core/proxy';
import { PaymentMethodTypeStore } from '@digiwall/goliath-contacts';
import { CurrentCompanyStore, GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';
import { DocRoutesService } from '@digiwall/goliath-documents/config';
import {
  CustomerProjecAllDocumentsViewService,
  CustomerProjectAllDocumentsViewDto,
} from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types';
import { CompanyService, DocRole } from '@digiwall/goliath-core/proxy';

@Component({
  selector: 'document-all-documents-with-projects-list',
  templateUrl: './all-docs-with-projects-list.component.html',
  styles: `
    :host {
      height: calc(100vh - 202px);
      display: flex;
      align-items: stretch;
      flex-direction: column;
    }
  `,
  providers: [DwGridDataSourceService, SubscriptionService],
  imports: [CommonModule, ListPageComponent, AgGridComponent, LocalizationModule],
})
export class AllDocumentsWithProjectsListComponent extends BaseDapperReadListPage<
  CustomerProjectAllDocumentsViewDto,
  CustomerProjecAllDocumentsViewService
> {
  customerProjectDisplayNumber = input.required<string>();

  protected readonly languageStore = inject(LanguageStore);
  protected readonly currentCompanyStore = inject(CurrentCompanyStore);
  protected readonly paymentMethodTypeStore = inject(PaymentMethodTypeStore);
  protected readonly documentEnumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  protected readonly coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);
  protected readonly companyService = inject(CompanyService);

  protected readonly grid = viewChild.required<AgGridComponent>('agGrid');

  override colDefs: ColDef[] = [
    ...AuditedColumns(this.localizationService),
    {
      field: 'documentDate',
      headerName: this.localizationService.instant('Doc::CustomerQuote:DocumentDate'),
      type: FieldTypes.Date,
    },
    {
      field: 'docDisplayNumber',
      headerName: this.localizationService.instant('::Number'),
      type: FieldTypes.String,
    },
    {
      field: 'isClosed',
      headerName: this.localizationService.instant('Doc::CustomerQuote:IsClosed'),
      type: FieldTypes.Boolean,
    },
    {
      field: 'isDraft',
      headerName: this.localizationService.instant('Doc::CustomerQuote:IsDraft'),
      type: FieldTypes.Boolean,
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
    },
    {
      field: 'note',
      headerName: this.localizationService.instant('::Note'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'dayBook',
      headerName: this.localizationService.instant('Doc::DayBook'),
      type: FieldTypes.String,
    },
    {
      field: 'salesPerson',
      headerName: this.localizationService.instant('GoliathCore::SalesPerson'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'currencyCode',
      headerName: this.localizationService.instant('::Currency'),
      type: FieldTypes.String,
    },
    {
      field: 'isActive',
      headerName: this.localizationService.instant('::IsActive'),
      type: FieldTypes.Boolean,
      hide: true,
    },
    {
      field: 'companySite',
      headerName: this.localizationService.instant('::CompanySite'),
      type: FieldTypes.String,
    },
    {
      field: 'languageId',
      headerName: this.localizationService.instant('GoliathCore::Language'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.languageStore.entities,
      } as IEnumerationCellRendererParams,
      hide: true,
    },
    {
      field: 'notifiedCount',
      headerName: this.localizationService.instant('Doc::NotifiedCount'),
      type: FieldTypes.Number,
      hide: true,
    },
    {
      field: 'lastNotificationDate',
      headerName: this.localizationService.instant('Doc::LastNotificationDate'),
      type: FieldTypes.DateTime,
      hide: true,
    },
    {
      field: 'financialDiscount',
      headerName: this.localizationService.instant('::FinancialDiscount'),
      type: FieldTypes.Currency,
      hide: true,
    },
    {
      field: 'financialDiscountDays',
      headerName: this.localizationService.instant('::FinancialDiscountDays'),
      type: FieldTypes.Number,
      hide: true,
    },
    {
      field: 'totalAmount',
      colId: 'TotalAmount_RoundedPrice',
      headerName: this.localizationService.instant('Doc::TotalAmount'),
      type: FieldTypes.Currency,
    },
    {
      field: 'totalAmountVatIncluded',
      colId: 'TotalAmountVatIncluded_RoundedPrice',
      headerName: this.localizationService.instant('Doc::TotalAmountVatIncl'),
      type: FieldTypes.Currency,
      hide: true,
    },
    {
      field: 'totalGain',
      colId: 'TotalGain_RoundedPrice',
      headerName: this.localizationService.instant('Doc::TotalGain'),
      type: FieldTypes.Currency,
      hide: true,
    },
    {
      field: 'margin',
      colId: 'Margin_RoundedPrice',
      headerName: this.localizationService.instant('Doc::Margin'),
      type: FieldTypes.Percentage,
      hide: true,
    },
    {
      field: 'thirdPartyCode',
      headerName: this.localizationService.instant('Contact::ThirdParty:Code'),
      type: FieldTypes.String,
    },
    {
      field: 'thirdPartyName',
      headerName: this.localizationService.instant('Contact::ThirdParty:Name'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'thirdPartyAddress',
      colId: 'DeliveryAddress_Street',
      headerName: this.localizationService.instant('Doc::CustomerAddress'),
      type: FieldTypes.String,
    },
    {
      field: 'thirdPartyPostalCode',
      headerName: this.localizationService.instant('Doc::CustomerPostalCode'),
      type: FieldTypes.String,
    },
    {
      field: 'thirdPartyLocality',
      headerName: this.localizationService.instant('Doc::CustomerLocality'),
      type: FieldTypes.String,
    },
    {
      field: 'thirdPartyContactFirstName',
      headerName: this.localizationService.instant('Contact::ContactFirstName'),
      type: FieldTypes.String,
    },
    {
      field: 'thirdPartyContactLastName',
      headerName: this.localizationService.instant('Contact::ContactLastName'),
      type: FieldTypes.String,
    },
    {
      field: 'retainBackOrders',
      headerName: this.localizationService.instant('Doc::CustomerQuote:BackOrder'),
      type: FieldTypes.Boolean,
      hide: true,
    },
    {
      field: 'isBlocked',
      headerName: this.localizationService.instant('Doc::CustomerQuote:IsBlocked'),
      type: FieldTypes.Boolean,
      hide: true,
    },
    {
      field: 'blockedReason',
      headerName: this.localizationService.instant('Doc::CustomerQuote:BlockedReason'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'litigation',
      headerName: this.localizationService.instant('Doc::CustomerQuote:Litigation'),
      type: FieldTypes.Boolean,
      hide: true,
    },
    {
      field: 'litigationReason',
      headerName: this.localizationService.instant('Doc::CustomerQuote:LitigationReason'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'totalWeight',
      headerName: this.localizationService.instant('Doc::TotalWeight'),
      type: FieldTypes.Number,
      cellRendererParams: {
        decimals: this.currentCompanyStore.weightDecimals,
      },
      hide: true,
    },
    {
      field: 'paymentTerm',
      headerName: this.localizationService.instant('GoliathCore::Menu:PaymentTerms'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'invoiceTerm',
      headerName: this.localizationService.instant('GoliathCore::Menu:InvoiceTerms'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'paymentMethodId',
      headerName: this.localizationService.instant('GoliathCore::PaymentMethod'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.paymentMethodTypeStore.entities,
      } as IEnumerationCellRendererParams,
      hide: true,
    },
    {
      field: 'deliveryVehicleName',
      headerName: this.localizationService.instant('Doc::DeliveryVehicle'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'reference',
      headerName: this.localizationService.instant('Doc::Reference'),
      type: FieldTypes.String,
    },
    {
      field: 'totalVatAmount',
      headerName: this.localizationService.instant('Doc::TotalTVA'),
      type: FieldTypes.Currency,
    },
    {
      field: 'allowPartialDelivery',
      headerName: this.localizationService.instant('Doc::CustomerQuote:PartialDelivery'),
      type: FieldTypes.Boolean,
      hide: true,
    },
    {
      field: 'directDebit',
      headerName: this.localizationService.instant('Contact::ThirdParty:DirectDebit'),
      type: FieldTypes.Boolean,
      hide: true,
    },
    {
      field: 'representative',
      headerName: this.localizationService.instant('Contact::ThirdParty:RepresentativeId'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'representativeCommision',
      headerName: this.localizationService.instant('Contact::ThirdParty:RepresentativeCommission'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'deliveryModeId',
      headerName: this.localizationService.instant('Contact::ThirdParty:DeliveryModeId'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.coreEnumerationTypeStore.deliveryModes,
      } as IEnumerationCellRendererParams,
    },
    {
      field: 'invoicingAddress',
      colId: 'InvoicingAddress_Street',
      headerName: this.localizationService.instant('Doc::InvoicingAddress'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'invoicingPostalCode',
      headerName: this.localizationService.instant('Doc::InvoicingPostalCode'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'invoicingLocality',
      headerName: this.localizationService.instant('Doc::InvoicingLocality'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'docOriginId',
      headerName: this.localizationService.instant('::DocOrigin'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.documentEnumerationTypeStore.docOrigins,
      } as IEnumerationCellRendererParams,
    },
    {
      field: 'pointOfSaleCode',
      headerName: this.localizationService.instant('Doc::CustomerQuote:PointOfSale'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'pointOfSaleName',
      headerName: this.localizationService.instant('Doc::CustomerQuote:PointOfSaleName'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'thirdPartyClassificationValue1Name',
      headerName: this.localizationService.instant('Contact::ClassificationType1'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'thirdPartyClassificationValue2Name',
      headerName: this.localizationService.instant('Contact::ClassificationType2'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'customerProjectDisplayNumber',
      headerName: this.localizationService.instant('Doc::CustomerProject'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'productionFolderStatus',
      headerName: this.localizationService.instant('Doc::QuoteStatus'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.documentEnumerationTypeStore.projectStatus,
      } as IEnumerationCellRendererParams,
    },
    {
      field: 'responsible',
      headerName: this.localizationService.instant('Doc::ProductionFolderDoc:Responsible'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'manufacturer',
      headerName: this.localizationService.instant('Doc::ProductionFolderDoc:Manufacturer'),
      type: FieldTypes.String,
      hide: true,
    },
    {
      field: 'preferredProductionDate',
      headerName: this.localizationService.instant(
        'Doc::ProductionFolderDoc:PreferredProductionDate',
      ),
      type: FieldTypes.Date,
      hide: true,
    },
    {
      field: 'preferredProductionWeek',
      headerName: this.localizationService.instant(
        'Doc::ProductionFolderDoc:PreferredProductionWeek',
      ),
      type: FieldTypes.Number,
      hide: true,
    },
    {
      field: 'productionSite',
      headerName: this.localizationService.instant('Doc::ProductionFolderDoc:ProductionSite'),
      type: FieldTypes.String,
      hide: true,
    },
  ];

  constructor(
    protected docService: CustomerProjecAllDocumentsViewService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected confirmation: ConfirmationService,
    protected localizationService: LocalizationService,
    datasource: DwGridDataSourceService,
  ) {
    super(docService, datasource);
    effect(() => {
      this.customerProjectDisplayNumber();
      this.grid()?.refreshGrid();
    });

    this.setupColumnUpdates(this.grid, this.getClassificationColDefUpdates());
  }

  public override rowClicked($event: CustomerProjectAllDocumentsViewDto) {
    this.navigateToDocument($event.id, $event.docRole);
  }
  public override selectedRowsClicked($event: Array<CustomerProjectAllDocumentsViewDto>) {
    $event.forEach(entry => this.navigateToDocument(entry.id, entry.docRole));
  }

  private navigateToDocument(id: string, docRole: string): void {
    const baseUrl = window.location.href.replace(this.router.url, '');
    window.open(baseUrl + `${this.getDocumentPath(docRole)}/${id}`, '_blank');
  }

  private getDocumentPath(docRole: string): string {
    // ServiceWorkOrder and ProductionFolder docs live under their own root modules
    // (/service-work-order, /production), not under /documents like the other DocRoles.
    if (docRole === DocRole.ServiceWorkOrder) {
      return '/service-work-order/service-work-orders';
    }
    if (docRole === DocRole.ProductionFolder) {
      return '/production/folder-docs';
    }
    return `/documents${DocRoutesService.routePaths[docRole]}`;
  }

  protected override getQueryObservable() {
    const baseObservable = super.getQueryObservable();
    return (query: DwGridRequestDto) => {
      if (query?.request != null && this.customerProjectDisplayNumber()?.length > 0) {
        query.request.filterModel ??= {};
        query.request.filterModel.customerProjectDisplayNumber = {
          filter: this.customerProjectDisplayNumber() as unknown as object,
          filterType: 'text',
          type: 'equals',
        } as FilterModel;
      }
      return baseObservable(query);
    };
  }

  protected getClassificationColDefUpdates(): Observable<ColDef[]> {
    return this.companyService.getTopSecondThirdPartyClassificationType().pipe(
      map(types => {
        types.forEach((classificationType, i) => {
          const field = `thirdPartyClassificationValue${i + 1}Name`;
          const col = this.colDefs.find(x => x.field === field);
          if (col) col.headerName = classificationType.name.current;
        });
        return this.colDefs;
      }),
    );
  }
}
